import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import './Sidebar.css';
import { 
  Drawer, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Paper
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SchoolIcon from '@mui/icons-material/School';
import GroupIcon from '@mui/icons-material/Group';
import ClassIcon from '@mui/icons-material/Class';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SettingsIcon from '@mui/icons-material/Settings';
import LaunchIcon from '@mui/icons-material/Launch';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path?: string;
  external?: boolean;
  url?: string;
  children?: MenuItem[];
}

const drawerWidth = 280;

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState('dashboard');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();
  const { user, isGlobalAdmin, isOrgAdmin, isInstitutionAdmin } = useAuth();
  const { isMobile } = useIsMobile();
  
  const baseMenuItems: MenuItem[] = [
    { id: 'dashboard', label: t('sidebar.dashboard'), icon: <SchoolIcon />, path: '/dashboard' },
    { id: 'students', label: t('sidebar.students'), icon: <GroupIcon />, path: '/students' },
    { id: 'classes', label: t('sidebar.classes'), icon: <ClassIcon />, path: '/classes' },
    { id: 'class-work', label: t('sidebar.classWork'), icon: <AssignmentIcon />, path: '/class-work' },
    { id: 'google-classroom', label: t('sidebar.googleClassroom'), icon: <MenuBookIcon />, external: true, url: 'https://classroom.google.com' }
  ];

  // Build admin menu based on role
  const adminMenuItems: MenuItem[] = [];
  
  // Global Admin menu
  if (isGlobalAdmin) {
    adminMenuItems.push({
      id: 'admin',
      label: t('sidebar.systemAdmin'),
      icon: <AdminPanelSettingsIcon />,
      children: [
        { id: 'organizations', label: t('sidebar.organizations'), icon: <BusinessIcon />, path: '/admin/organizations' },
        { id: 'institutions', label: t('sidebar.institutions'), icon: <AccountBalanceIcon />, path: '/admin/institutions' },
        { id: 'user-management', label: t('sidebar.userManagement'), icon: <PersonIcon />, path: '/admin/users' }
      ]
    });
  }
  
  if (isOrgAdmin) {
    adminMenuItems.push({
      id: 'org-admin',
      label: t('sidebar.orgAdmin'),
      icon: <AdminPanelSettingsIcon />,
      children: [
        { id: 'institutions', label: t('sidebar.institutions'), icon: <AccountBalanceIcon />, path: '/admin/institutions' },
        { id: 'org-users', label: t('sidebar.userManagement'), icon: <PersonIcon />, path: '/admin/users' }
      ]
    });
  }
  
  if (isInstitutionAdmin) {
    adminMenuItems.push({
      id: 'institution-admin',
      label: t('sidebar.institutionAdmin'),
      icon: <AdminPanelSettingsIcon />,
      children: [
        { id: 'institution-users', label: t('sidebar.userManagement'), icon: <PersonIcon />, path: '/admin/users' }
      ]
    });
  }

  // Keep main menu items separate from admin items (admin goes at bottom)
  const menuItems = [...baseMenuItems];
  const allMenuItems = [...baseMenuItems, ...adminMenuItems];

  // Update active item based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Check main menu items first
    const currentItem = allMenuItems.find(item => item.path === currentPath);
    if (currentItem) {
      setActiveItem(currentItem.id);
      return;
    }
    
    // Check nested menu items
    for (const item of allMenuItems) {
      if (item.children) {
        const childItem = item.children.find(child => child.path === currentPath);
        if (childItem) {
          setActiveItem(childItem.id);
          // Expand the parent menu
          setExpandedItems(prev => new Set(Array.from(prev).concat(item.id)));
          return;
        }
      }
    }
    
    // Check if we're on settings
    if (currentPath === '/settings') {
      setActiveItem('settings');
      return;
    }
    
    // Default to dashboard for root or unknown paths
    setActiveItem('dashboard');
  }, [location.pathname]);

  const handleMenuClick = (item: MenuItem) => {
    if (item.children) {
      // Toggle expanded state for parent items
      const newExpanded = new Set(expandedItems);
      if (newExpanded.has(item.id)) {
        newExpanded.delete(item.id);
      } else {
        newExpanded.add(item.id);
      }
      setExpandedItems(newExpanded);
    } else if (item.external && item.url) {
      // Open external link in new tab
      window.open(item.url, '_blank', 'noopener,noreferrer');
      if (isMobile) setMobileOpen(false);
    } else if (item.path) {
      // Navigate internally
      setActiveItem(item.id);
      navigate(item.path);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawerContent = (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="logo">
          <div className="logo-icon" aria-hidden="true">
            <AutoStoriesIcon sx={{ fontSize: 26, color: 'white' }} />
          </div>
          <span className="logo-text">{t('sidebar.classroomBridge')}</span>
        </div>
      </div>
      
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {menuItems.map((item) => (
            <li key={item.id} className="nav-item">
              <button
                className={`nav-link ${activeItem === item.id ? 'active' : ''} ${item.external ? 'external-link' : ''} ${item.children ? 'has-children' : ''}`}
                onClick={() => handleMenuClick(item)}
                aria-label={item.external ? `${item.label} - Opens in new window` : item.label}
                aria-expanded={item.children ? expandedItems.has(item.id) : undefined}
                aria-current={activeItem === item.id ? 'page' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                {item.children && (
                  <span className={`expand-icon ${expandedItems.has(item.id) ? 'expanded' : ''}`} aria-hidden="true">
                    ▼
                  </span>
                )}
                {item.external && (
                  <span className="external-icon" aria-hidden="true">
                    <LaunchIcon sx={{ fontSize: 14 }} />
                  </span>
                )}
              </button>
              {item.children && expandedItems.has(item.id) && (
                <ul className="nav-submenu">
                  {item.children.map((child) => (
                    <li key={child.id} className="nav-subitem">
                      <button
                        className={`nav-sublink ${activeItem === child.id ? 'active' : ''}`}
                        onClick={() => handleMenuClick(child)}
                        aria-label={child.label}
                        aria-current={activeItem === child.id ? 'page' : undefined}
                      >
                        <span className="nav-subicon" aria-hidden="true">{child.icon}</span>
                        <span className="nav-sublabel">{child.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
      
      <div className="sidebar-footer">
        {/* Admin menu items at bottom */}
        {adminMenuItems.length > 0 && (
          <ul className="nav-list footer-nav-list">
            {adminMenuItems.map((item) => (
              <li key={item.id} className="nav-item">
                <button
                  className={`nav-link ${activeItem === item.id ? 'active' : ''} ${item.children ? 'has-children' : ''}`}
                  onClick={() => handleMenuClick(item)}
                  aria-label={item.label}
                  aria-expanded={item.children ? expandedItems.has(item.id) : undefined}
                  aria-current={activeItem === item.id ? 'page' : undefined}
                >
                  <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                  {item.children && (
                    <span className={`expand-icon ${expandedItems.has(item.id) ? 'expanded' : ''}`} aria-hidden="true">
                      ▼
                    </span>
                  )}
                </button>
                {item.children && expandedItems.has(item.id) && (
                  <ul className="nav-submenu">
                    {item.children.map((child) => (
                      <li key={child.id} className="nav-subitem">
                        <button
                          className={`nav-sublink ${activeItem === child.id ? 'active' : ''}`}
                          onClick={() => handleMenuClick(child)}
                          aria-label={child.label}
                          aria-current={activeItem === child.id ? 'page' : undefined}
                        >
                          <span className="nav-subicon" aria-hidden="true">{child.icon}</span>
                          <span className="nav-sublabel">{child.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
        
        {/* Settings button */}
        <button 
          className={`settings-btn ${activeItem === 'settings' ? 'active' : ''}`}
          onClick={() => {
            setActiveItem('settings');
            navigate('/settings');
            if (isMobile) setMobileOpen(false);
          }}
          aria-label={t('sidebar.settings')}
          aria-current={activeItem === 'settings' ? 'page' : undefined}
        >
          <span className="settings-icon" aria-hidden="true"><SettingsIcon /></span>
          <span className="settings-label">{t('sidebar.settings')}</span>
        </button>
      </div>
    </div>
  );

  const bottomNavItems = [
    { label: t('sidebar.dashboard'), icon: <SchoolIcon />, path: '/dashboard', id: 'dashboard' },
    { label: t('sidebar.students'), icon: <GroupIcon />, path: '/students', id: 'students' },
    { label: t('sidebar.classes'), icon: <ClassIcon />, path: '/classes', id: 'classes' },
    { label: t('sidebar.classWork'), icon: <AssignmentIcon />, path: '/class-work', id: 'class-work' },
    { label: t('sidebar.more'), icon: <MoreHorizIcon />, path: '', id: 'more' },
  ];

  const bottomNavValue = bottomNavItems.findIndex(item => item.id === activeItem);

  return (
    <>
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: 'var(--cb-primary, #2f9e7a)',
            left: 0,
            right: 0
          }}
        >
          <Toolbar sx={{ minHeight: '56px', px: 1 }}>
            <AutoStoriesIcon sx={{ mr: 1, fontSize: 22 }} />
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: '1rem' }}>
              Classroom Bridge
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Desktop sidebar */}
      <Box
        component="nav"
        sx={{ 
          width: { xs: 0, md: drawerWidth }, 
          flexShrink: { md: 0 },
          minWidth: 0,
          margin: 0,
          padding: 0,
          display: { xs: 'none', md: 'block' }
        }}
        aria-label="Main navigation"
      >
        <Drawer
          variant="permanent"
          sx={{
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Mobile bottom navigation */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            borderTop: '1px solid var(--cb-border)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          elevation={8}
        >
          <BottomNavigation
            showLabels
            value={bottomNavValue >= 0 ? bottomNavValue : false}
            onChange={(_event, newValue) => {
              const item = bottomNavItems[newValue];
              if (item.id === 'more') {
                setMobileOpen(!mobileOpen);
              } else if (item.path) {
                navigate(item.path);
                setMobileOpen(false);
              }
            }}
            sx={{
              height: 64,
              bgcolor: 'rgba(255, 255, 255, 0.97)',
              backdropFilter: 'blur(10px)',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                color: 'var(--cb-muted)',
                '&.Mui-selected': {
                  color: 'var(--cb-primary)',
                },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.65rem',
                '&.Mui-selected': {
                  fontSize: '0.7rem',
                  fontWeight: 700,
                },
              },
            }}
          >
            {bottomNavItems.map((item) => (
              <BottomNavigationAction
                key={item.id}
                label={item.label}
                icon={item.icon}
                aria-label={item.label}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}

      {/* Mobile overflow drawer for "More" items (settings, admin, external links) */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '70vh',
            },
          }}
        >
          <Box sx={{ p: 1, pt: 2, pb: 'calc(80px + env(safe-area-inset-bottom))' }}>
            <Box sx={{ width: 40, height: 4, bgcolor: 'var(--cb-border)', borderRadius: 2, mx: 'auto', mb: 2 }} />
            {drawerContent}
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
